import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt } from 'react-icons/fa';
import powershinelogo from '../../assets/images/powershine-logo.png';
import powerfixLogo from '../../assets/images/power-fix-solutions-logo.png';

export default function ClientsSection() {
  const { t } = useTranslation('home');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const clients = [
    {
      name: "Powershine",
      url: "https://www.powershine.nl",
      domain: "www.powershine.nl",
      logo: powershinelogo,
      logoBg: "bg-blue-50",
    },
    {
      name: "Power Fix Solutions",
      url: "https://www.power-fix-solutions.com",
      domain: "www.power-fix-solutions.com",
      logo: powerfixLogo,
      logoBg: "bg-gray-50",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-24 px-6 sm:px-12 bg-white relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            {t('clients.badge')}
          </span>
          <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            {t('clients.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              {t('clients.titleHighlight')}
            </span>
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            {t('clients.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {clients.map((client, index) => (
            <ClientCard
              key={index}
              client={client}
              isVisible={isVisible}
              delay={index * 150}
              visitLabel={t('clients.visitWebsite')}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ClientCard({ client, isVisible, delay, visitLabel }) {
  return (
    <a
      href={client.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.6s ease-out',
      }}
    >
      <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:-translate-y-2">
        <div className={`flex items-center justify-center p-10 ${client.logoBg}`}>
          <img
            src={client.logo}
            alt={client.name}
            className="h-28 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        <div className="px-8 py-5 border-t border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors duration-300">
            {client.name}
          </h3>
          <div className="flex items-center justify-between mt-1">
            <p className="text-sm text-gray-400">{client.domain}</p>
            <div className="flex items-center gap-1.5 text-primary text-xs font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300">
              <span>{visitLabel}</span>
              <FaExternalLinkAlt />
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}
